import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { conditionalSocket } from "../../../types";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity, SystemEvent } from "@erp/shared-types";

/**
 * Create AP Invoice Draft Step
 *
 * Creates an AP invoice record (status: DRAFT) with line items from extracted data.
 * Includes idempotency checks to avoid creating duplicates.
 *
 * Emits AP_INVOICE_DRAFT_CREATED only when a new draft is persisted — reruns
 * and dedup/thread matches that resolve to an existing invoice do not emit.
 * The event carries the same `apInvoice` / `invoiceNumber` / `referenceIds`
 * the step outputs, so a handling workflow can subscribe without re-extracting.
 *
 * A document classified as an invoice update does not create or edit an
 * invoice and does not emit. `existingInvoice` carries the invoice the update
 * refers to, when one is found. `apInvoice` is not taken on that branch.
 *
 * Note: This is a backend-only step that requires database access.
 * The handler is registered in backend/src/workflows/steps/handlers/
 */
export const createAPInvoiceDraftStep = defineStepConfig({
  type: "create_ap_invoice_draft",
  name: "Create AP Invoice Draft",
  description: "Create AP invoice draft record with line items",
  category: "Data Access",
  libraryModule: "AccountsPayable",
  inputSchema: z.object({
    extractionResultId: z.uuid(),
  }),

  outputSchema: z.object({
    // Conditional for intentional no-draft branches, such as invoice updates.
    // Failed or empty extraction throws instead of skipping downstream steps.
    apInvoice: conditionalSocket(entityOfType(SystemEntity.AP_INVOICE)),
    // Taken only when the document was classified as an invoice update and an
    // existing invoice was found. Untaken for new drafts, reruns, and updates
    // that match no invoice — downstream steps wired here are skipped.
    existingInvoice: conditionalSocket(
      entityOfType(SystemEntity.AP_INVOICE),
    ).describe(
      "The existing invoice this document updates. Set only when the document was classified as an invoice update and a matching invoice was found. The invoice is not created or edited.",
    ),
    invoiceNumber: z.string().optional(),
    referenceIds: z
      .array(z.string())
      .optional()
      .describe("Array of PO numbers or other references from the invoice"),
  }),

  configSchema: z.object({
    // Future: add config for matching thresholds, auto-approval rules
  }),

  emits: [SystemEvent.AP_INVOICE_DRAFT_CREATED] as const,
});
